import React, { useState } from 'react';
import { useAuth } from '../utils/auth';
import { useNavigate } from 'react-router-dom';
import { TextField, Button, Container, Typography, Box, Paper, Alert } from '@mui/material';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { login, googleSignIn } = useAuth();
  const navigate = useNavigate();

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(email, password);
      navigate('/home');
    } catch (error) {
      console.error('Failed to log in', error);
      setError('Unable to sign in. Check your email and password, then try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setBusy(true);
    try {
      await googleSignIn();
      navigate('/home');
    } catch (error) {
      console.error('Failed to sign in with Google', error);
      setError('Google sign-in did not complete. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', py: 4 }}>
      <Paper sx={{ width: '100%', maxWidth: 480, p: { xs: 3, sm: 5 }, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 4 }}>
          <Box sx={{ width: 44, height: 44, borderRadius: 2.5, display: 'grid', placeItems: 'center', color: '#FFF', fontWeight: 800, background: 'linear-gradient(135deg, #087F6B, #38C7BE)' }}>F</Box>
          <Box>
            <Typography variant="h6" lineHeight={1.2}>Flora</Typography>
            <Typography variant="body2" color="text.secondary">Admin workspace</Typography>
          </Box>
        </Box>
        <Typography component="h1" variant="h4">Welcome back</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1, mb: 2 }}>Sign in to manage Flora content and campaigns.</Typography>
        {error && <Alert severity="error">{error}</Alert>}
        <Box component="form" onSubmit={handleEmailLogin}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type="password"
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Button
            type="submit"
            fullWidth
            disabled={busy}
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
          >
            Sign In
          </Button>
          <Button
            fullWidth
            disabled={busy}
            variant="outlined"
            onClick={handleGoogleSignIn}
            sx={{ mt: 1, mb: 2 }}
          >
            Sign in with Google
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default Login;
